import { Mesh, Group, Vector3, Euler, PerspectiveCamera, WebGLRenderTarget, Scene, NearestFilter, RGBAFormat, FloatType } from 'three';
import React, { useRef, useState, useEffect } from 'react';
import { useGLTF, useFBO, useProgress } from '@react-three/drei';
import { GLTF } from 'three-stdlib';
import { createPortal, useFrame, useThree } from '@react-three/fiber';

import { useTeamMemberContext } from 'context/Team';
import FBOScene from '@/3D/scenes/FBOScene/FBOScene';
import { useTransition } from '@/3D/effects/Transition/Transition';

type GLTFResult = GLTF & {
  nodes: {
    Cube004: Mesh;
    Cube004_1: Mesh;
  };
};

const TeamTV: React.FC<JSX.IntrinsicElements['group']> = (
  props: JSX.IntrinsicElements['group']
) => {
  const group = useRef<Group>(null);
  const { nodes } = useGLTF('/objects/TV.glb') as unknown as GLTFResult;
  const screen = useRef<Mesh>(new Mesh());
  const { size, gl, camera, scene } = useThree();
  const [position, setPosition] = useState<Vector3>(new Vector3());
  const [rotation, setRotation] = useState<Euler>(new Euler());
  const { setContext } = useTeamMemberContext();
  // this is the camera and the scene in the fbo scene
  const fboCam = React.useRef<PerspectiveCamera>(null);
  const fboScene = React.useMemo(() => {
    const scene = new Scene();
    return scene;
  }, []);
  const { progress } = useProgress();

  // 4:3 aspect ratio
  enum ScreenRes {
    Width = 360,
    Height = 240
  }
  const fboRenderTarget = useFBO(ScreenRes.Width, ScreenRes.Height, {
    minFilter: NearestFilter,
    magFilter: NearestFilter,
    format: RGBAFormat,
    stencilBuffer: false,
    depthBuffer: true,
    type: FloatType,
    samples: 4
  });

  // these are the effects for the tv screen
  const {
    config: { outerGL, outerScene, outerCam }
  } = useTeamMemberContext();

  // just removing transition effects for now :o
  const [base] = useTransition({
    cam: fboCam.current || new PerspectiveCamera(),
    scene: fboScene || new Scene(),
    renderTarget: fboRenderTarget || new WebGLRenderTarget(size.width, size.height)
  });

  // link tv scene to render target
  useFrame(state => {
    const { gl } = state;
    if (fboCam?.current) {
      gl.setRenderTarget(fboRenderTarget);
      gl.render(fboScene, fboCam.current);
      gl.setRenderTarget(null);
    }
  });

  useFrame(() => {
    base.render();
    if (!base.autoRenderToScreen) {
      // need to re-render outer scene since new texture has been added each frame
      if (outerGL && outerScene && outerCam) {
        outerGL.setRenderTarget(null);
        outerGL.render(outerScene, outerCam);
      }
    }
  }, -1);

  // watch window resize and set camera/tv position accordingly
  useEffect(() => {
    if (fboCam.current) {
      fboCam.current.zoom = 4;
      fboCam.current.lookAt(0, 0, 0);
      fboCam.current.updateProjectionMatrix();

      // mobile & tablet
      if (size.width <= 992) {
        setPosition(new Vector3(0, 0, -1.25));
        setRotation(new Euler(Math.PI / 2, 0, 0));
        return;
      }
      setPosition(new Vector3(2, 0.2, -1.75));
      setRotation(new Euler(Math.PI / 2, 0, 0.1));
    }
  }, [size]);

  useEffect(() => {
    setContext({
      outerGL: gl,
      outerScene: scene,
      outerCam: camera
    });
  }, [gl, scene, camera]);

  return (
    <group ref={group} {...props} dispose={null} position={position} rotation={rotation}>
      {/* this is the camera in the scene on the tv screen */}
      <perspectiveCamera ref={fboCam} position={[20, 0.05, 0]} />
      <mesh receiveShadow name="Cube004" geometry={nodes.Cube004.geometry}>
        <meshStandardMaterial />
      </mesh>
      {/* this is the scene rendered on the tv screen */}
      {createPortal(<FBOScene />, fboScene)}
      {/* render target material */}
      {progress >= 100 ? (
        <>
          <mesh name="Cube004_1" geometry={nodes.Cube004_1.geometry} ref={screen}>
            <meshStandardMaterial
              map={base.outputBuffer.texture ? base.outputBuffer.texture : fboRenderTarget.texture}
            />
          </mesh>
          {/* glass material */}
          <mesh castShadow receiveShadow name="Cube004_2" geometry={nodes.Cube004_1.geometry}>
            <meshPhysicalMaterial
              clearcoat={1}
              clearcoatRoughness={0.09}
              toneMapped={true}
              transmission={1}
              thickness={5}
              roughness={0.15}
              emissive={'#fff'}
              emissiveMap={
                base.outputBuffer.texture ? base.outputBuffer.texture : fboRenderTarget.texture
              }
              emissiveIntensity={0.39}
            />
          </mesh>
        </>
      ) : null}
    </group>
  );
};

export default TeamTV;
